import { useConfirmation } from "src/sudokit/components/ConfirmationDialog/ConfirmationProvider";
import classnames from "classnames";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import SVG from "react-inlinesvg";
import CommentModel from "../../../models/Comment";
import DisplayName from "../../../models/DisplayName";
/*
 * Use the same derived-count helper as the toolbar badge so the sidebar filter total and
 * toolbar do not disagree after History changes which anchors exist.
 */
import { getVisibleOpenCommentCount, useCommentStore } from "../../../stores/commentStore";
import { useProjectStore } from "../../../stores/projectStore";
import { useStoryBibleStore } from "../../../stores/storyBibleStore";
import { useSudowriteStore } from "../../../stores/sudowriteStore";
import NewComment from "../../Comment/NewComment";
import Draft from "../Draft";
import DraftComparison from "../DraftComparison";
import Thumbs from "../DraftThumbs";
import EditorToolbar from "../EditorToolbar";
import { LinkBubble } from "../EditorToolbar/LinkPopover/LinkBubble";
import ProseGenerator from "../ProseGenerator";
import ChapterEditor from "../ChapterEditor";
import DocumentLinkingMenu from "./DocumentLinkingMenu";
import { EditableDocumentTitle } from "./EditableDocumentTitle";
import { BackgroundContainer, EditorHeaderContainer, EditorContainerContainer } from "./styles";

import { useComments } from "../../../hooks/useComments";
import { useToggleCommentMode } from "../../../hooks/useToggleCommentMode";
import { shouldAutoOpenCommentSidebar } from "../../../hooks/shouldAutoOpenCommentSidebar";
import CommentSidebar from "../CommentSidebar";
import { useToast } from "../../ToastWrapper";
import { EditorContainerProvider } from "./EditorContainerContext";
import StickyNoteAnchor from "../../StickyTour/StickyNoteAnchor";
import { AnchorIds } from "../../StickyTour/TourTypes";
import { useComparisonRestore } from "../../../hooks/useComparisonRestore";
import Auth from "src/Auth.js";
import { sendAppEvent } from "src/Utils";
import * as Sentry from "@sentry/react";
import { canUndoCommentInEditor } from "src/utils/tiptapSuggestion";
import { applyFeedbackComment } from "./handleApplyFeedback";
import { useAnnotationNavigation } from "../ChapterEditor/hooks/useAnnotationNavigation";
import { pickMidlineAnnotationId } from "./pickMidlineAnnotation";
import { collectAnnotationRects } from "./collectAnnotationRects";
import { shouldRestoreOppositeScrollJump, shouldTreatKeydownAsScrollIntent } from "./scrollKeyIntent";
import { setActiveManuscriptCommentThreadIdForAgent, setCommentSidebarOrderForAgent } from "../../../lib/activeManuscriptCommentForAgent";
import {
  COMMENT_FILTER_TYPES,
  filterCommentsByType,
  getCommentCountsByFilter,
  getCommentFilterLabel,
  getCommentFilterType,
} from "../CommentSidebar/commentStatus";
import { GrammarPopover } from "../GrammarPopover";
import { StyleSettingsDialog } from "src/components/StyleSettings";
import { shouldOpenCommentSidebarForFirstFeedbackComment } from "../feedbackSidebarVisibility";
import { FeedbackApplyConfirmationDialog } from "./FeedbackApplyConfirmationDialog";
import { createProjectPrintDocument, removeProjectPrintDocument } from "./projectPrintDocument";

const MAX_NEAREST_ANNOTATION_FALLBACK_PX = 520;
const NEAREST_ANNOTATION_FALLBACK_VIEWPORT_RATIO = 0.55;

const parseAnnotationIdsFromAttribute = (value) =>
  String(value || "")
    .split(/\s+/)
    .map((id) => id.trim())
    .filter(Boolean);

const getCommentCountsWithFilterValue = (currentCounts, filterType, count) => {
  if (!currentCounts) return currentCounts;
  return {
    [COMMENT_FILTER_TYPES.OPEN]: Number(currentCounts[COMMENT_FILTER_TYPES.OPEN]) || 0,
    [COMMENT_FILTER_TYPES.RESOLVED]: Number(currentCounts[COMMENT_FILTER_TYPES.RESOLVED]) || 0,
    [COMMENT_FILTER_TYPES.IGNORED]: Number(currentCounts[COMMENT_FILTER_TYPES.IGNORED]) || 0,
    [filterType]: Math.max(0, Number(count) || 0),
  };
};

const getReopenedCommentForNavigation = (comment) => ({
  ...comment,
  status: COMMENT_FILTER_TYPES.OPEN,
  resolvedAt: null,
  resolved_at: null,
  acceptedAt: null,
  accepted_at: null,
  declinedAt: null,
  declined_at: null,
});

export default function Editor({
  editor,
  editorReady,
  insertEditor,
  onUndo,
  onRedo,
  focus, //
  setFocus,
  range,
  leftBarCollapsed,
  disabled = false,
  documentId,
  editorComputedStyles,
  project,
  contents,
  centerColumnRef,
  editorToolbarRef,
  editorRef,
  editorEndRef,
  showEditableTitle,
  showThumbs,
  setShowThumbs,
  toggleFindAndReplaceOpen,
  titleEditorRef,
  user,
  setRightBarCollapsed,
  hideLinkingMenu,
  inTrashView,
  ydoc,
  existingCards,
  existingCardsLoaded,
  onEditorLayoutChange,
  toggleSpellCheck,
  addDraftPlaceholderCard,
  appendDraftPlaceholderContent,
  linkDraftPlaceholderToCardId,
  finalizeDraftPlaceholder,
  removeDraftPlaceholderCard,
}) {
  const confirmAction = useConfirmation(documentId);
  const [dismissed, setDismissed] = useState();
  const [intro, setIntro] = useState(true);
  const [expandedCardId, setExpandedCardId] = useState(null);
  const [feedbackApplyConfirmation, setFeedbackApplyConfirmation] = useState(null);
  const feedbackApplyConfirmationResolverRef = useRef(null);
  const [selectedCommentFilter, setSelectedCommentFilter] = useState(COMMENT_FILTER_TYPES.OPEN);
  /** Set only when expanding from an agentic chat `sudowrite://comment/...` link so the sidebar wiggle runs */
  const [sidebarWiggleCommentId, setSidebarWiggleCommentId] = useState(null);
  /**
   * When an expanded card disappears (e.g. apply/dismiss), remember it briefly.
   * If undo brings that same thread back, auto-expand it again.
   */
  const recentlyRemovedExpandedCardRef = useRef({ id: null, removedAt: 0 });
  const commentNavigationLookupRef = useRef(null);
  const annotationNavigationLookupRef = useRef(null);
  const pendingSequentialNavigationRef = useRef(null);
  const editableDocumentTitleRef = useRef(null);
  const titleWrapperRef = useRef(null);
  const draftPanelRef = useRef(null);
  const initialHashCommentId = useRef(null);
  if (initialHashCommentId.current === null) {
    try {
      const m = window.location.hash.match(/#(.*)/);
      initialHashCommentId.current = m
        ? (new URLSearchParams(m[1]).get("comment") ?? "")
        : "";
    } catch {
      initialHashCommentId.current = "";
    }
  }
  const [draftPanelHeight, setDraftPanelHeight] = useState(0);
  const recentInvocations = useStoryBibleStore((state) => state.recentInvocations);
  const lastInvocation = useStoryBibleStore((state) => state.lastInvocation);
  const setLastInvocation = useStoryBibleStore((state) => state.setLastInvocation);
  const isStreaming = useStoryBibleStore((state) => state.isStreaming);
  const requesting = useStoryBibleStore((state) => state.requesting);
  const proseGeneratorActive = useStoryBibleStore((state) => state.proseGeneratorActive);

  const documentTitle = contents?.find((item) => item.id === documentId)?.title;
  const editorLabel = documentTitle && !/^Untitled( \d+)?$/.test(documentTitle)
    ? `Editor: ${documentTitle}`
    : "Document editor";

  const setFeedbackOpen = useProjectStore((state) => state.setFeedbackOpen);
  const setFeedbackInvocation = useProjectStore((state) => state.setFeedbackInvocation);
  const commentSidebarWidth = useProjectStore(
    (state) => state.commentSidebarWidthPreview ?? state.commentSidebarWidth,
  );
  const editorContainerRef = useRef(null);
  const { showComments, toggleComments } = useToggleCommentMode();
  const hasAutoExpandedCommentForVisibleSessionRef = useRef(false);

  // Expose the open comment thread id for agent context marking (no reordering).
  useEffect(() => {
    if (!showComments) {
      setActiveManuscriptCommentThreadIdForAgent(null);
      return;
    }
    setActiveManuscriptCommentThreadIdForAgent(expandedCardId);
  }, [documentId, showComments, expandedCardId]);

  const requestFeedbackApplyConfirmation = useCallback((details) => new Promise((resolve) => {
    feedbackApplyConfirmationResolverRef.current?.(false);
    feedbackApplyConfirmationResolverRef.current = resolve;
    setFeedbackApplyConfirmation(details);
  }), []);

  const settleFeedbackApplyConfirmation = useCallback((confirmed) => {
    const resolve = feedbackApplyConfirmationResolverRef.current;
    feedbackApplyConfirmationResolverRef.current = null;
    setFeedbackApplyConfirmation(null);
    resolve?.(confirmed);
  }, []);

  useEffect(() => {
    setFeedbackApplyConfirmation(null);
    return () => {
      const resolve = feedbackApplyConfirmationResolverRef.current;
      feedbackApplyConfirmationResolverRef.current = null;
      resolve?.(false);
    };
  }, [documentId]);

  // Comments State
  const comments = useCommentStore((state) => state.comments);
  const waitingForFeedbackComments = useCommentStore((state) => state.waitingForFeedbackComments);
  /*
   * These are the server's complete open root-comment IDs, not only IDs from the currently loaded
   * page. They let the sidebar count visible comments accurately without loading every comment
   * body.
   */
  const openCommentAnnotationIds = useCommentStore((state) => state.openCommentAnnotationIds);
  const showNewCommentBox = useCommentStore((state) => state.showNewCommentBox);
  const setDOMDocument = useCommentStore((state) => state.setDOMDocument);
  const [newComment, setNewComment] = useState("");
  const displayName = useCommentStore((state) => state.displayName);
  const setDisplayName = useCommentStore((state) => state.setDisplayName);
  const subscription = useSudowriteStore((state) => state.subscription);
  const commentTitleMaxWidthOffset = subscription.hasAccessToCleanCopy && showComments ? commentSidebarWidth : 0;
  const setCommenter = useCommentStore((state) => state.setCommenter);
  const setIsDocumentOwner = useCommentStore((state) => state.setIsDocumentOwner);
  const setIsSharedDocument = useCommentStore((state) => state.setIsSharedDocument);
  const commenter = useCommentStore((state) => state.commenter);
  const setSetToast = useCommentStore((state) => state.setSetToast);
  const { setToast } = useToast();
  // Use the comments hook
  const {
    commentsLoaded,
    isLoading: commentsLoading,
    hasMoreComments,
    isLoadingMoreComments,
    loadMoreComments,
    commentCounts: paginatedCommentCounts,
    updateCommentCounts,
    resetCommentPagination,
    preserveCommentsForNextLoad,
  } = useComments({
    isAuthor: true,
    documentId,
    userId: user?.uid,
    filterType: selectedCommentFilter,
  });

  const {
    comparisonMode,
    setComparisonMode,
    comparisonVisible,
    setComparisonVisible,
    comparisonEditorsRef,
    comparisonInvocationIdRef,
    streamBSceneIndexRef,
    handlePick,
  } = useComparisonRestore({
    documentId,
    existingCards,
    existingCardsLoaded,
    editor,
    editorEndRef,
  });

  const tiptap = useCommentStore((state) => state.tiptapEditor);
  const resolveComment = useCommentStore((state) => state.resolveComment);
  const undoResolvedComment = useCommentStore((state) => state.undoResolvedComment);
  const annotationUndoSnapshots = useCommentStore((state) => state.annotationUndoSnapshots);
  const deleteComment = useCommentStore((state) => state.deleteComment);
  const DOMDocument = useCommentStore((state) => state.DOMDocument);
  const newAnnotationId = useCommentStore((state) => state.newAnnotationId);
  /*
   * The client-side half of the count intersection comes from the active ProseMirror manuscript.
   * Readiness distinguishes unsynced emptiness from a restored version that truly contains zero
   * annotations.
   */
  const documentAnnotationIds = useCommentStore((state) => state.documentAnnotationIds);
  const documentAnnotationsReady = useCommentStore((state) => state.documentAnnotationsReady);

  // Build a map of annotationId -> DOM position for sorting and insertion.
  const annotationPositionMap = useMemo(() => {
    const map = new Map();
    if (!showComments || !DOMDocument || comments.length === 0) return map;
    // Scope to the manuscript editor: annotation spans rendered elsewhere in
    // the main document (e.g. Document History previews) must not influence
    // sidebar sort order or insert indexes.
    const targetAnnotationIds = new Set(
      comments
        .map((comment) => String(comment?.annotationId || "").trim())
        .filter(Boolean),
    );
    if (targetAnnotationIds.size === 0) return map;

    const queryRoot = editorContainerRef.current || DOMDocument.getElementById?.("editorContainer") || DOMDocument;
    const annotationElements = queryRoot.querySelectorAll("span[data-annotation-id]");
    let domIndex = 0;
    for (const el of annotationElements) {
      const ids = parseAnnotationIdsFromAttribute(el.getAttribute("data-annotation-id"));
      ids.forEach((id) => {
        if (targetAnnotationIds.has(id) && !map.has(id)) {
          map.set(id, domIndex);
        }
      });
      if (map.size === targetAnnotationIds.size) break;
      domIndex += 1;
    }
    return map;
  }, [comments, DOMDocument, showComments]);

  // Sort comments by their annotation's position in the editor DOM (document order).
  const sortedComments = useMemo(() => {
    if (annotationPositionMap.size === 0 || comments.length === 0) return comments;
    return [...comments].sort((a, b) => {
      const posA = annotationPositionMap.get(a.annotationId) ?? Infinity;
      const posB = annotationPositionMap.get(b.annotationId) ?? Infinity;
      return posA - posB;
    });
  }, [comments, annotationPositionMap]);
  /*
   * Override only the Open total with the current-marks/open-row intersection. Preserve the
   * existing Resolved/Ignored totals because their cards intentionally remain browsable without
   * marks. Loaded local statuses reconcile optimistic changes, and the pending composer mark is
   * excluded. Dependencies include both metadata sources so restoring text alone refreshes the
   * displayed total.
   */
  const commentCounts = useMemo(() => {
    const counts = paginatedCommentCounts || getCommentCountsByFilter(sortedComments);
    return {
      ...counts,
      [COMMENT_FILTER_TYPES.OPEN]: getVisibleOpenCommentCount({
        comments: sortedComments,
        documentAnnotationIds,
        documentAnnotationsReady,
        openCommentAnnotationIds,
        newAnnotationId,
        fallbackCount: counts?.[COMMENT_FILTER_TYPES.OPEN],
      }),
    };
  }, [documentAnnotationIds, documentAnnotationsReady, newAnnotationId, openCommentAnnotationIds, paginatedCommentCounts, sortedComments]);
  const updateLocalCommentCountsForStatusChange = useCallback((fromType, toType = null) => {
    if (!fromType && !toType) return;
    if (fromType && toType && fromType === toType) return;
    updateCommentCounts((currentCounts) => {
      if (!currentCounts) return currentCounts;
      const nextCounts = {
        [COMMENT_FILTER_TYPES.OPEN]: Number(currentCounts[COMMENT_FILTER_TYPES.OPEN]) || 0,
        [COMMENT_FILTER_TYPES.RESOLVED]: Number(currentCounts[COMMENT_FILTER_TYPES.RESOLVED]) || 0,
        [COMMENT_FILTER_TYPES.IGNORED]: Number(currentCounts[COMMENT_FILTER_TYPES.IGNORED]) || 0,
      };

      if (fromType) {
        nextCounts[fromType] = Math.max(0, (nextCounts[fromType] || 0) - 1);
      }
      if (toType) {
        nextCounts[toType] = (nextCounts[toType] || 0) + 1;
      }
      return nextCounts;
    });
  }, [updateCommentCounts]);
  const visibleComments = useMemo(
    () => filterCommentsByType(sortedComments, selectedCommentFilter),
    [sortedComments, selectedCommentFilter],
  );
  const previousFeedbackCommentCountRef = useRef(comments.length);
  const pendingChatPaginationRequestRef = useRef({ key: null, loadedCount: -1 });

  useEffect(() => {
    const previousCommentCount = previousFeedbackCommentCountRef.current;
    previousFeedbackCommentCountRef.current = comments.length;

    if (shouldOpenCommentSidebarForFirstFeedbackComment({
      hasAccessToCleanCopy: subscription.hasAccessToCleanCopy,
      waitingForFeedbackComments,
      previousCommentCount,
      commentCount: comments.length,
      showComments,
    })) {
      toggleComments(true, { userInitiated: false });
    }
  }, [comments.length, subscription.hasAccessToCleanCopy, waitingForFeedbackComments, showComments, toggleComments]);

  const clearPendingChatPaginationRequest = useCallback(() => {
    pendingChatPaginationRequestRef.current = { key: null, loadedCount: -1 };
  }, []);

  const requestNextPendingChatCommentPage = useCallback((commentId) => {
    const requestKey = `${documentId}:${commentId}`;
    const loadedCount = comments.length;
    const previousRequest = pendingChatPaginationRequestRef.current;
    if (previousRequest.key === requestKey && previousRequest.loadedCount === loadedCount) {
      return;
    }
    pendingChatPaginationRequestRef.current = { key: requestKey, loadedCount };
    void loadMoreComments().then((result) => {
      if (result?.success !== false || result?.skipped) return;
      const currentRequest = pendingChatPaginationRequestRef.current;
      if (currentRequest?.key === requestKey && currentRequest.loadedCount === loadedCount) {
        clearPendingChatPaginationRequest();
      }
    });
  }, [clearPendingChatPaginationRequest, comments.length, documentId, loadMoreComments]);

  // Keep agent context ordering aligned with the visible sidebar ordering in the editor.
  useEffect(() => {
    setCommentSidebarOrderForAgent(
      documentId,
      sortedComments.map((c) => String(c.id)),
    );
  }, [documentId, sortedComments]);

  // Find where the NewComment box should be inserted in the sorted list.
  const newCommentInsertIndex = useMemo(() => {
    if (!newAnnotationId || annotationPositionMap.size === 0) return 0;
    const newPos = annotationPositionMap.get(newAnnotationId) ?? Infinity;
    let index = 0;
    for (const comment of sortedComments) {
      const pos = annotationPositionMap.get(comment.annotationId) ?? Infinity;
      if (pos > newPos) break;
      index++;
    }
    return index;
  }, [newAnnotationId, sortedComments, annotationPositionMap]);

  // Drop chat-only wiggle target when the expanded thread no longer matches (sidebar, editor, scroll, arrows).
  useEffect(() => {
    if (!sidebarWiggleCommentId) return;
    if (sidebarWiggleCommentId !== expandedCardId) {
      setSidebarWiggleCommentId(null);
    }
  }, [expandedCardId, sidebarWiggleCommentId]);

  // Annotation navigation — powers sidebar arrows and card-click-to-scroll
  const { currentAnnotation, goToNextComment, goToPrevComment, scrollToAnnotation, scrollToAnnotationById } = useAnnotationNavigation({
    centerColumnRef,
    editorToolbarRef,
    editorRef,
    isLoading: commentsLoading || isLoadingMoreComments,
    isSticky: false,
  });

  const setComments = useCommentStore((state) => state.setComments);
  const handleDeleteAllComments = useCallback(async (filterType = COMMENT_FILTER_TYPES.OPEN) => {
    const filterLabel = getCommentFilterLabel(filterType).toLowerCase();
    sendAppEvent("Comment - Delete All Clicked", {
      filter_type: filterType
    });
    await confirmAction({
      title: "Clear comments",
      message: `Are you sure you want to clear all ${filterLabel} comments? This cannot be undone.`,
      actionLabel: "Clear comments",
      destructive: true
    }, async () => {
      try {
        const deleteResult = await Auth.fetchWithIdToken(`/api/comments/document/${documentId}/all?type=${encodeURIComponent(filterType)}`, {
          method: "DELETE"
        });
        // Comments may arrive while the custom confirmation is open.
        // Never replace a new document's store with this request's results.
        const latest = useCommentStore.getState();
        if (latest.documentId !== documentId) return;
        const currentComments = latest.allComments || latest.comments;
        const nextComments = currentComments.filter(comment => getCommentFilterType(comment) !== filterType);
        setComments(nextComments);
        resetCommentPagination(nextComments, currentCounts => getCommentCountsWithFilterValue(currentCounts, filterType, 0));
        const loadedAnnotationIds = currentComments.filter(comment => getCommentFilterType(comment) === filterType).map(comment => comment?.annotationId).filter(Boolean);
        const annotationIdsToUnset = Array.isArray(deleteResult?.annotationIds) ? deleteResult.annotationIds : loadedAnnotationIds;
        annotationIdsToUnset.forEach(annotationId => {
          if (annotationId) {
            useCommentStore.getState()._unsetAnnotation(annotationId);
          }
        });
      } catch (err) {
        Sentry.captureException(err, {
          tags: {
            func: "handleDeleteAllComments"
          }
        });
        setToast("Failed to delete all comments");
      }
    });
  }, [documentId, comments, setComments, resetCommentPagination, setToast, confirmAction]);
  useEffect(() => {
    if (showComments) {
      if (useProjectStore.getState().pendingChatCommentNavigation) return;
      setSelectedCommentFilter(COMMENT_FILTER_TYPES.OPEN);
    }
  }, [showComments]);


  /** Apply an AI feedback suggestion — replaces annotated text */
  const handleApplyFeedback = useCallback((comment) => {
    const previousFilterType = getCommentFilterType(comment);
    const updateCountsAfterResolve = (result) => {
      if (result?.success) {
        updateLocalCommentCountsForStatusChange(previousFilterType, COMMENT_FILTER_TYPES.RESOLVED);
      }
    };
    const captureResolveError = (error) => {
      Sentry.captureException(error, { tags: { func: "handleApplyFeedbackResolve" } });
    };
    applyFeedbackComment(comment, {
      tiptap,
      resolveComment: (annotationId) => (
        resolveComment(annotationId)
          .then(updateCountsAfterResolve)
          .catch(captureResolveError)
      ),
      withSuppressedMarkRemoval: (annotationId, fn) => (
        useCommentStore.getState().withSuppressedMarkRemoval(annotationId, fn)
      ),
      confirmChangedTarget: requestFeedbackApplyConfirmation,
      notifyInvalidSuggestion: () => setToast("This suggestion isn't a safe text replacement, so it wasn't applied."),
    });
  }, [tiptap, resolveComment, requestFeedbackApplyConfirmation, setToast, updateLocalCommentCountsForStatusChange]);

  /** Dismiss an AI feedback suggestion */
  const handleDismissFeedback = useCallback((comment) => {
    const previousFilterType = getCommentFilterType(comment);
    void resolveComment(comment.annotationId, {
      status: COMMENT_FILTER_TYPES.IGNORED,
      persist: false,
    }).then((result) => {
      if (result?.success) {
        updateLocalCommentCountsForStatusChange(previousFilterType, COMMENT_FILTER_TYPES.IGNORED);
      }
    }).catch((error) => {
      Sentry.captureException(error, { tags: { func: "handleDismissFeedbackResolve" } });
    });
    Auth.fetchWithIdToken(`/api/comments/${comment.id}/decline`, { method: "POST" }).catch(() => {
      setToast("Failed to dismiss suggestion");
    });
  }, [resolveComment, setToast, updateLocalCommentCountsForStatusChange]);

  /** Delete a user comment */
  const handleDeleteUserComment = useCallback(async (commentId, annotationId) => {
    const comment = sortedComments.find((c) => String(c.id) === String(commentId));
    const previousFilterType = comment ? getCommentFilterType(comment) : null;
    const result = await deleteComment(commentId, annotationId);
    if (result?.success && previousFilterType) {
      updateLocalCommentCountsForStatusChange(previousFilterType, null);
    }
  }, [deleteComment, sortedComments, updateLocalCommentCountsForStatusChange]);

  const handleUndoComment = useCallback(async (comment) => {
    const result = await undoResolvedComment(comment);
    if (!result?.success) {
      if (!result?.alreadyToasted) {
        setToast(result?.message ?? "We can't undo this comment.");
      }
      sendAppEvent("Comment - Undo Failed", {
        comment_id: comment.id,
        reason: result?.alreadyToasted ? "api_error" : result?.reason ?? "unknown",
      });
      return;
    }
    sendAppEvent("Comment - Undo Successful", { comment_id: comment.id });
    updateLocalCommentCountsForStatusChange(getCommentFilterType(comment), COMMENT_FILTER_TYPES.OPEN);
    const reopenedComment =
      useCommentStore.getState().allComments.find((c) => String(c.id) === String(comment.id)) ||
      getReopenedCommentForNavigation(comment);
    useProjectStore.getState().setPendingChatCommentNavigation({
      commentId: String(comment.id),
      documentId,
      filterType: COMMENT_FILTER_TYPES.OPEN,
    });
    clearPendingChatPaginationRequest();
    preserveCommentsForNextLoad(reopenedComment, COMMENT_FILTER_TYPES.OPEN);
    setSelectedCommentFilter(COMMENT_FILTER_TYPES.OPEN);
    setExpandedCardId(comment.id);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        scrollToAnnotationById(comment.annotationId, "center", { behavior: "auto" });
      });
    });
  }, [
    clearPendingChatPaginationRequest,
    documentId,
    preserveCommentsForNextLoad,
    scrollToAnnotationById,
    setToast,
    undoResolvedComment,
    updateLocalCommentCountsForStatusChange,
  ]);

  const canUndoComment = useCallback((comment) => {
    const snapshotPassage = annotationUndoSnapshots?.[comment.annotationId]?.passage;
    const result = canUndoCommentInEditor(tiptap, comment, { snapshotPassage });
    return result.canUndo === true;
  }, [annotationUndoSnapshots, tiptap]);

  /** Expand a card and scroll editor to its annotation */
  const handleExpandCard = useCallback((commentId) => {
    setExpandedCardId(commentId);
    if (!commentId) {
      setSidebarWiggleCommentId(null);
    }
    if (!commentId) return;
    const comment = sortedComments.find((c) => c.id === commentId);
    if (!comment) return;
    scrollToAnnotationById(comment.annotationId, "center", { behavior: "auto" });
  }, [sortedComments, scrollToAnnotationById]);

  // Default-open one thread per visible sidebar session so reply UI is immediately visible.
  useEffect(() => {
    if (!showComments) {
      hasAutoExpandedCommentForVisibleSessionRef.current = false;
      return;
    }
    if (expandedCardId) {
      hasAutoExpandedCommentForVisibleSessionRef.current = true;
      return;
    }
    if (waitingForFeedbackComments) return;
    if (hasAutoExpandedCommentForVisibleSessionRef.current) return;
    if (visibleComments.length === 0) return;

    hasAutoExpandedCommentForVisibleSessionRef.current = true;
    handleExpandCard(visibleComments[0].id);
  }, [showComments, expandedCardId, waitingForFeedbackComments, visibleComments, handleExpandCard]);

  /** Scroll editor to a comment's annotation (used when clicking an already-expanded card) */
  const handleScrollToAnnotation = useCallback((commentId) => {
    const comment = sortedComments.find((c) => c.id === commentId);
    if (!comment) return;
    scrollToAnnotationById(comment.annotationId, "center", { behavior: "auto" });
  }, [sortedComments, scrollToAnnotationById]);

  /** Navigate to next card sequentially (in document order) and scroll editor to its annotation */
  const handleNavigateDown = useCallback(() => {
    if (visibleComments.length === 0) {
      if (hasMoreComments && !isLoadingMoreComments) {
        pendingSequentialNavigationRef.current = { direction: "down", fromId: null, loadedCount: 0 };
        void loadMoreComments().then((result) => {
          if (result?.success === false && !result?.skipped) {
            pendingSequentialNavigationRef.current = null;
          }
        });
      }
      return;
    }
    const currentIdx = expandedCardId ? visibleComments.findIndex(c => c.id === expandedCardId) : -1;
    const nextIdx = currentIdx + 1;
    if (nextIdx >= visibleComments.length) {
      if (hasMoreComments && !isLoadingMoreComments) {
        pendingSequentialNavigationRef.current = {
          direction: "down",
          fromId: visibleComments[visibleComments.length - 1]?.id ?? null,
          loadedCount: visibleComments.length,
        };
        void loadMoreComments().then((result) => {
          if (result?.success === false && !result?.skipped) {
            pendingSequentialNavigationRef.current = null;
          }
        });
      }
      return;
    }
    handleExpandCard(visibleComments[nextIdx].id);
  }, [
    expandedCardId,
    handleExpandCard,
    hasMoreComments,
    isLoadingMoreComments,
    loadMoreComments,
    visibleComments,
  ]);

  /** Navigate to previous card sequentially (in document order) and scroll editor to its annotation */
  const handleNavigateUp = useCallback(() => {
    if (visibleComments.length === 0) return;
    const currentIdx = expandedCardId ? visibleComments.findIndex(c => c.id === expandedCardId) : visibleComments.length;
    const prevIdx = currentIdx - 1;
    if (prevIdx < 0) return;
    handleExpandCard(visibleComments[prevIdx].id);
  }, [visibleComments, expandedCardId, handleExpandCard]);

  useEffect(() => {
    const pending = pendingSequentialNavigationRef.current;
    if (!pending || pending.direction !== "down") return;
    if (isLoadingMoreComments) return;

    const fromIndex = pending.fromId
      ? visibleComments.findIndex((comment) => comment.id === pending.fromId)
      : -1;
    const nextComment =
      fromIndex >= 0
        ? visibleComments[fromIndex + 1] || null
        : (visibleComments.length > pending.loadedCount ? visibleComments[pending.loadedCount] : null);

    if (nextComment) {
      pendingSequentialNavigationRef.current = null;
      handleExpandCard(nextComment.id);
      return;
    }

    if (!hasMoreComments) {
      pendingSequentialNavigationRef.current = null;
    }
  }, [handleExpandCard, hasMoreComments, isLoadingMoreComments, visibleComments]);

  const clearPendingCommentNavigation = useCallback(() => {
    clearPendingChatPaginationRequest();
    useProjectStore.getState().setPendingChatCommentNavigation(null);
  }, [clearPendingChatPaginationRequest]);

  const openLoadedComment = useCallback((commentId, comment) => {
    clearPendingCommentNavigation();
    if (!showComments) {
      toggleComments(true);
    }
    setSidebarWiggleCommentId(commentId);
    setExpandedCardId(commentId);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        scrollToAnnotationById(comment.annotationId, "center", { behavior: "auto" });
      });
    });
  }, [clearPendingCommentNavigation, scrollToAnnotationById, showComments, toggleComments]);

  const queueCommentNavigation = useCallback(async (commentId, knownTargetComment = null) => {
    const normalizedCommentId = String(commentId || "").trim();
    if (!normalizedCommentId) return false;

    const loadedComment = sortedComments.find(
      (c) => String(c.id) === normalizedCommentId && (!c.documentId || String(c.documentId) === String(documentId)),
    );
    if (loadedComment) {
      openLoadedComment(normalizedCommentId, loadedComment);
      return true;
    }

    useProjectStore.getState().setPendingChatCommentNavigation({
      commentId: normalizedCommentId,
      documentId,
      filterType: selectedCommentFilter,
      resolvingFilter: true,
    });
    if (!showComments) {
      toggleComments(true);
    }

    const lookupKey = knownTargetComment ? null : `${documentId}:${normalizedCommentId}`;
    if (lookupKey && commentNavigationLookupRef.current === lookupKey) return false;
    if (lookupKey) {
      commentNavigationLookupRef.current = lookupKey;
    }

    try {
      const targetComment = knownTargetComment || await CommentModel.getById(normalizedCommentId);
      if (useCommentStore.getState().documentId !== documentId) return false;
      if (!targetComment || String(targetComment.documentId) !== String(documentId)) {
        clearPendingCommentNavigation();
        return true;
      }

      const targetCommentId = String(targetComment.parentId || targetComment.id || normalizedCommentId);
      const targetFilterType = getCommentFilterType(targetComment);
      let targetThreadComment = sortedComments.find((c) => String(c.id) === targetCommentId) || null;
      if (!targetThreadComment && String(targetComment.id) === targetCommentId) {
        targetThreadComment = targetComment;
      }
      if (!targetThreadComment && targetComment.parentId) {
        targetThreadComment = await CommentModel.getById(targetCommentId).catch(() => null);
      }
      if (targetThreadComment && String(targetThreadComment.documentId) === String(documentId)) {
        preserveCommentsForNextLoad(targetThreadComment, targetFilterType);
      } else {
        targetThreadComment = null;
      }

      useProjectStore.getState().setPendingChatCommentNavigation({
        commentId: targetCommentId,
        documentId,
        filterType: targetFilterType,
      });
      setSidebarWiggleCommentId(targetCommentId);
      setExpandedCardId(targetCommentId);

      if (targetFilterType !== selectedCommentFilter) {
        clearPendingChatPaginationRequest();
        setSelectedCommentFilter(targetFilterType);
        return false;
      }

      if (targetThreadComment) {
        const commentState = useCommentStore.getState();
        const currentComments = commentState.allComments || [];
        if (!currentComments.some((comment) => String(comment.id) === targetCommentId)) {
          commentState.setComments([...currentComments, targetThreadComment]);
        }
        openLoadedComment(targetCommentId, targetThreadComment);
        return true;
      }

      if (hasMoreComments && !isLoadingMoreComments) {
        requestNextPendingChatCommentPage(targetCommentId);
        return false;
      } else if (!commentsLoading && commentsLoaded && !isLoadingMoreComments) {
        const loadedTargetComment = sortedComments.find(
          (c) => String(c.id) === targetCommentId && (!c.documentId || String(c.documentId) === String(documentId)),
        );
        if (!loadedTargetComment) return false;
        openLoadedComment(targetCommentId, loadedTargetComment);
        return true;
      }
    } catch (error) {
      clearPendingCommentNavigation();
      Sentry.captureException(error, { tags: { func: "queueCommentNavigation" } });
      return false;
    } finally {
      if (lookupKey && commentNavigationLookupRef.current === lookupKey) {
        commentNavigationLookupRef.current = null;
      }
    }
    return false;
  }, [
    clearPendingChatPaginationRequest,
    clearPendingCommentNavigation,
    commentsLoaded,
    commentsLoading,
    documentId,
    hasMoreComments,
    isLoadingMoreComments,
    openLoadedComment,
    preserveCommentsForNextLoad,
    requestNextPendingChatCommentPage,
    selectedCommentFilter,
    showComments,
    sortedComments,
    toggleComments,
  ]);

  /** Agentic chat links on the current document: open sidebar if needed, expand card, scroll. */
  useEffect(() => {
    const handler = (e) => {
      const detail = e.detail || {};
      const { commentId, documentId: targetDocumentId } = detail;
      if (!commentId) return;
      if (targetDocumentId && targetDocumentId !== documentId) return;
      if (!subscription.hasAccessToCleanCopy) return;
      const comment = sortedComments.find((c) => String(c.id) === String(commentId));
      if (comment) {
        openLoadedComment(commentId, comment);
        return;
      }
      void queueCommentNavigation(commentId);
    };
    window.addEventListener("sudowrite:open-comment", handler);
    return () => window.removeEventListener("sudowrite:open-comment", handler);
  }, [
    documentId,
    openLoadedComment,
    queueCommentNavigation,
    subscription.hasAccessToCleanCopy,
    sortedComments,
  ]);

  /** After navigating from chat to another document, expand once comments list includes the target thread. */
  useEffect(() => {
    const pending = useProjectStore.getState().pendingChatCommentNavigation;
    if (!pending || pending.documentId !== documentId) return;
    if (!subscription.hasAccessToCleanCopy) return;
    if (pending.filterType && pending.filterType !== selectedCommentFilter) {
      setSelectedCommentFilter(pending.filterType);
      return;
    }
    if (pending.resolvingFilter) return;
    if (commentsLoading || !commentsLoaded) return;
    // SWR may still return the previous document's threads for a render after `documentId` changes.
    // Do not clear `pending` or give up until the store matches this editor document.
    const commentsBelongToThisDocument =
      comments.length === 0 || comments.every((c) => !c.documentId || c.documentId === documentId);
    if (!commentsBelongToThisDocument) {
      return;
    }
    const comment = sortedComments.find(
      (c) => String(c.id) === String(pending.commentId) && (!c.documentId || c.documentId === documentId),
    );
    if (!comment) {
      if (!pending.filterType) {
        void queueCommentNavigation(pending.commentId);
        return;
      }
      if (isLoadingMoreComments) return;
      if (hasMoreComments) {
        requestNextPendingChatCommentPage(pending.commentId);
        return;
      }
      clearPendingCommentNavigation();
      return;
    }
    openLoadedComment(pending.commentId, comment);
  }, [
    documentId,
    comments,
    commentsLoading,
    commentsLoaded,
    clearPendingCommentNavigation,
    hasMoreComments,
    isLoadingMoreComments,
    openLoadedComment,
    queueCommentNavigation,
    requestNextPendingChatCommentPage,
    selectedCommentFilter,
    sortedComments,
    subscription.hasAccessToCleanCopy,
  ]);

  /** On new-tab cross-series loads, open the sidebar to the comment specified in the URL hash. */
  useEffect(() => {
    const commentId = initialHashCommentId.current;
    if (!commentId) return;
    if (!subscription.hasAccessToCleanCopy) return;
    if (commentsLoading || !commentsLoaded) return;
    const comment = sortedComments.find((c) => String(c.id) === commentId);
    if (comment) {
      initialHashCommentId.current = "";
      openLoadedComment(commentId, comment);
      return;
    }
    void queueCommentNavigation(commentId).then((completed) => {
      if (completed) {
        initialHashCommentId.current = "";
      }
    });
  }, [
    commentsLoaded,
    commentsLoading,
    openLoadedComment,
    queueCommentNavigation,
    sortedComments,
    subscription.hasAccessToCleanCopy,
  ]);

  const queueAnnotationNavigation = useCallback(async (annotationId) => {
    const normalizedAnnotationId = String(annotationId || "").trim();
    if (!normalizedAnnotationId) return;

    const loadedComment = sortedComments.find((c) => String(c.annotationId) === normalizedAnnotationId);
    if (loadedComment) {
      setExpandedCardId(loadedComment.id);
      return;
    }

    const lookupKey = `${documentId}:annotation:${normalizedAnnotationId}`;
    if (annotationNavigationLookupRef.current === lookupKey) return;
    annotationNavigationLookupRef.current = lookupKey;

    try {
      const targetComment = await CommentModel.getByAnnotationId(normalizedAnnotationId);
      if (useCommentStore.getState().documentId !== documentId) return;
      if (!targetComment || String(targetComment.documentId) !== String(documentId)) {
        clearPendingCommentNavigation();
        return;
      }

      const targetCommentId = String(targetComment.parentId || targetComment.id);
      await queueCommentNavigation(targetCommentId, targetComment);
    } catch (error) {
      clearPendingCommentNavigation();
      Sentry.captureException(error, { tags: { func: "queueAnnotationNavigation" } });
    } finally {
      if (annotationNavigationLookupRef.current === lookupKey) {
        annotationNavigationLookupRef.current = null;
      }
    }
  }, [
    clearPendingCommentNavigation,
    documentId,
    queueCommentNavigation,
    sortedComments,
  ]);

  /** Handle click on annotation highlight in the editor container — expand corresponding card */
  useEffect(() => {
    if (!DOMDocument || !showComments) return;
    const handleAnnotationClick = (e) => {
      // Only annotation spans inside the manuscript editor count — snapshots
      // shown elsewhere (e.g. the Document History preview) render the same
      // marks in the main document now.
      if (editorContainerRef.current && !editorContainerRef.current.contains(e.target)) return;
      const target = e.target.closest("span[data-annotation-id]");
      if (!target) return;
      const annotationIds = (target.getAttribute("data-annotation-id") || "").split(" ");
      const firstId = annotationIds.find((id) => id.trim());
      if (!firstId) return;
      const comment = comments.find((c) => c.annotationId === firstId);
      if (!comment) {
        void queueAnnotationNavigation(firstId);
        return;
      }
      setExpandedCardId(comment.id);
    };
    DOMDocument.addEventListener("click", handleAnnotationClick);
    return () => DOMDocument.removeEventListener("click", handleAnnotationClick);
  }, [DOMDocument, comments, queueAnnotationNavigation, showComments]);

  // Track when the currently expanded card disappears from the sidebar list.
  useEffect(() => {
    if (!expandedCardId) return;
    const expandedStillExists = sortedComments.some((comment) => comment.id === expandedCardId);
    if (expandedStillExists) return;
    recentlyRemovedExpandedCardRef.current = {
      id: expandedCardId,
      removedAt: Date.now(),
    };
  }, [expandedCardId, sortedComments]);

  // If undo restores the most recently removed expanded comment, auto-open it again.
  useEffect(() => {
    if (expandedCardId) return;
    const { id: removedId, removedAt } = recentlyRemovedExpandedCardRef.current;
    if (!removedId) return;
    // Keep this narrowly scoped to likely undo/redo flows.
    if (Date.now() - removedAt > 15000) return;
    const restoredComment = sortedComments.find((comment) => comment.id === removedId);
    if (!restoredComment) return;
    if (!showComments) {
      toggleComments(true);
    }
    setExpandedCardId(removedId);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        scrollToAnnotationById(restoredComment.annotationId, "center", { behavior: "auto" });
      });
    });
    recentlyRemovedExpandedCardRef.current = { id: null, removedAt: 0 };
  }, [expandedCardId, sortedComments, showComments, toggleComments, scrollToAnnotationById]);

  /**
   * SW-8943 — Scroll-driven sidebar card sync.
   *
   * As the document scrolls, find the annotation span whose bounding rect is
   * crossing (or closest to) the parent viewport's midline and expand the
   * corresponding card. Reuses the existing click-to-expand mechanism —
   * we just call `setExpandedCardId` from a scroll listener.
   *
   * Runs only when the sidebar is actually showing, the editor container is
   * mounted, and there are visible comments to track.
   *
   * Explicit selection (sidebar/chat/annotation click, arrows, deep links):
   * pin midline sync until the user scrolls the manuscript stack again. A
   * short time-based suspend is not enough — smooth scroll can outlast it,
   * and a nearby annotation can still win the midline after scroll-to-comment.
   */
  const scrollDrivenIdRef = useRef(null);
  /** When true, scroll-driven midline must not change `expandedCardId` until the user moves the main scroll away from the post-navigation baseline. */
  const pinMidlineSyncUntilUserScrollRef = useRef(false);
  /** After explicit expand + scroll-to-comment settles: `{ scrollTop, windowScrollY }` or null while settling. */
  const postNavigationScrollBaselineRef = useRef(null);
  const baselineSettleTimerRef = useRef(null);
  const baselineFallbackTimerRef = useRef(null);
  const latestCenterScrollTopRef = useRef(0);
  const previousShowCommentsRef = useRef(showComments);
  const commentSidebarLayoutRestoreCancelledRef = useRef(false);
  const suppressMidlineInitialSyncRef = useRef(false);

  useEffect(() => {
    suppressMidlineInitialSyncRef.current = waitingForFeedbackComments;
  }, [waitingForFeedbackComments]);

  useEffect(() => {
    if (!showComments) {
      suppressMidlineInitialSyncRef.current = false;
    }
  }, [showComments]);

  useEffect(() => {
    const centerColumn = centerColumnRef?.current;
    if (!centerColumn) return;

    latestCenterScrollTopRef.current = centerColumn.scrollTop;
    const updateLatestScrollTop = () => {
      latestCenterScrollTopRef.current = centerColumn.scrollTop;
    };

    centerColumn.addEventListener("scroll", updateLatestScrollTop, { passive: true });
    return () => {
      centerColumn.removeEventListener("scroll", updateLatestScrollTop);
    };
  }, [centerColumnRef]);

  useLayoutEffect(() => {
    const centerColumn = centerColumnRef?.current;
    const showCommentsChanged = previousShowCommentsRef.current !== showComments;
    previousShowCommentsRef.current = showComments;
    if (!centerColumn || !showCommentsChanged) return;

    const savedScrollTop = latestCenterScrollTopRef.current;
    commentSidebarLayoutRestoreCancelledRef.current = false;
    const restoreScrollTop = () => {
      if (commentSidebarLayoutRestoreCancelledRef.current) return;
      if (Math.abs(centerColumn.scrollTop - savedScrollTop) <= 1) return;
      centerColumn.scrollTop = savedScrollTop;
      latestCenterScrollTopRef.current = savedScrollTop;
    };

    restoreScrollTop();
    const frame = window.requestAnimationFrame(restoreScrollTop);
    const timeouts = [80, 180, 360, 700].map((delay) => window.setTimeout(restoreScrollTop, delay));

    return () => {
      window.cancelAnimationFrame(frame);
      timeouts.forEach((timeout) => window.clearTimeout(timeout));
    };
  }, [showComments, centerColumnRef]);

  const clearBaselineTimers = useCallback(() => {
    if (baselineSettleTimerRef.current != null) {
      window.clearTimeout(baselineSettleTimerRef.current);
      baselineSettleTimerRef.current = null;
    }
    if (baselineFallbackTimerRef.current != null) {
      window.clearTimeout(baselineFallbackTimerRef.current);
      baselineFallbackTimerRef.current = null;
    }
  }, []);

  const releasePinnedMidlineSyncForUserScroll = useCallback(() => {
    if (!pinMidlineSyncUntilUserScrollRef.current) return;
    pinMidlineSyncUntilUserScrollRef.current = false;
    postNavigationScrollBaselineRef.current = null;
    clearBaselineTimers();
  }, [clearBaselineTimers]);

  const cancelPendingCenterColumnSmoothScroll = useCallback(() => {
    const centerColumn = centerColumnRef?.current;
    if (!centerColumn) return;
    const currentScrollTop = centerColumn.scrollTop;
    if (typeof centerColumn.scrollTo === "function") {
      centerColumn.scrollTo({ top: currentScrollTop, behavior: "auto" });
    } else {
      centerColumn.scrollTop = currentScrollTop;
    }
  }, [centerColumnRef]);

  useEffect(() => {
    if (!showComments) return;
    const centerColumn = centerColumnRef?.current;
    if (!centerColumn) return;

    const handleScrollIntent = () => {
      commentSidebarLayoutRestoreCancelledRef.current = true;
      cancelPendingCenterColumnSmoothScroll();
      releasePinnedMidlineSyncForUserScroll();
    };
    const handleKeydown = (event) => {
      if (!shouldTreatKeydownAsScrollIntent(event)) return;

      const beforeScrollTop = centerColumn.scrollTop;
      commentSidebarLayoutRestoreCancelledRef.current = true;
      cancelPendingCenterColumnSmoothScroll();
      releasePinnedMidlineSyncForUserScroll();

      const restoreOppositeJump = () => {
        if (
          shouldRestoreOppositeScrollJump({
            key: event.key,
            shiftKey: event.shiftKey,
            beforeScrollTop,
            afterScrollTop: centerColumn.scrollTop,
          })
        ) {
          centerColumn.scrollTop = beforeScrollTop;
          latestCenterScrollTopRef.current = beforeScrollTop;
        }
      };

      window.requestAnimationFrame(restoreOppositeJump);
      window.setTimeout(restoreOppositeJump, 80);
    };

    // Editor wheel/touch/key events bubble to centerColumn/window in the main
    // document, so the old iframe-window and iframe-document bindings are
    // covered by these two.
    centerColumn.addEventListener("wheel", handleScrollIntent, { passive: true });
    centerColumn.addEventListener("touchstart", handleScrollIntent, { passive: true });
    window.addEventListener("keydown", handleKeydown);

    return () => {
      centerColumn.removeEventListener("wheel", handleScrollIntent);
      centerColumn.removeEventListener("touchstart", handleScrollIntent);
      window.removeEventListener("keydown", handleKeydown);
    };
  }, [
    showComments,
    centerColumnRef,
    cancelPendingCenterColumnSmoothScroll,
    releasePinnedMidlineSyncForUserScroll,
  ]);

  // Mark external (non-scroll) selection changes — pin midline until user scrolls.
  useEffect(() => {
    if (scrollDrivenIdRef.current === expandedCardId) return;
    if (expandedCardId == null) {
      pinMidlineSyncUntilUserScrollRef.current = false;
      postNavigationScrollBaselineRef.current = null;
      clearBaselineTimers();
      return;
    }
    pinMidlineSyncUntilUserScrollRef.current = true;
    postNavigationScrollBaselineRef.current = null;
    clearBaselineTimers();
    baselineFallbackTimerRef.current = window.setTimeout(() => {
      baselineFallbackTimerRef.current = null;
      if (!pinMidlineSyncUntilUserScrollRef.current || postNavigationScrollBaselineRef.current !== null) return;
      const cc = centerColumnRef?.current;
      if (!cc) return;
      postNavigationScrollBaselineRef.current = {
        scrollTop: cc.scrollTop,
        windowScrollY: window.scrollY,
      };
    }, 2200);
    return () => {
      clearBaselineTimers();
    };
  }, [expandedCardId, centerColumnRef, clearBaselineTimers]);

  useEffect(() => {
    if (!showComments) return;
    if (waitingForFeedbackComments) return;
    if (!DOMDocument) return;
    if (!editorContainerRef.current) return;
    if (visibleComments.length === 0) return;

    const containerEl = editorContainerRef.current;

    // annotationId -> commentId
    const commentByAnnotationId = new Map();
    for (const c of visibleComments) {
      if (c.annotationId) commentByAnnotationId.set(c.annotationId, c.id);
    }

    let rafHandle = null;

    const captureBaselineAfterSettle = () => {
      if (!pinMidlineSyncUntilUserScrollRef.current || postNavigationScrollBaselineRef.current !== null) return;
      if (baselineSettleTimerRef.current != null) window.clearTimeout(baselineSettleTimerRef.current);
      baselineSettleTimerRef.current = window.setTimeout(() => {
        baselineSettleTimerRef.current = null;
        const cc = centerColumnRef?.current;
        if (!cc || !pinMidlineSyncUntilUserScrollRef.current || postNavigationScrollBaselineRef.current !== null) return;
        postNavigationScrollBaselineRef.current = {
          scrollTop: cc.scrollTop,
          windowScrollY: window.scrollY,
        };
      }, 160);
    };

    const tick = () => {
      rafHandle = null;
      // Extend suspension if a comment deletion is in progress (batch delete)
      const suppressedUntil = useCommentStore.getState().commentScrollSyncSuppressedUntil;
      if (typeof suppressedUntil === "number" && Date.now() < suppressedUntil) {
        return;
      }

      if (pinMidlineSyncUntilUserScrollRef.current) {
        const baseline = postNavigationScrollBaselineRef.current;
        if (baseline === null) {
          return;
        } else {
          const cc = centerColumnRef?.current;
          const scrollTop = cc?.scrollTop ?? 0;
          const wy = window.scrollY;
          const moved =
            Math.abs(scrollTop - baseline.scrollTop) > 6 || Math.abs(wy - baseline.windowScrollY) > 6;
          if (!moved) {
            return;
          }
          pinMidlineSyncUntilUserScrollRef.current = false;
          postNavigationScrollBaselineRef.current = null;
          clearBaselineTimers();
        }
      }

      if (!containerEl.isConnected) return;

      const containerRect = containerEl.getBoundingClientRect();
      const midlineY = window.innerHeight / 2;

      // If the editor is entirely off-screen, don't touch anything.
      if (containerRect.bottom < 0 || containerRect.top > window.innerHeight) return;

      // Annotation rects are already in the parent viewport space — no offset.
      const rects = collectAnnotationRects(containerEl, 0, commentByAnnotationId.keys());
      if (rects.length === 0) return;

      const maxFallbackDistance = Math.min(
        window.innerHeight * NEAREST_ANNOTATION_FALLBACK_VIEWPORT_RATIO,
        MAX_NEAREST_ANNOTATION_FALLBACK_PX
      );
      const nextId = pickMidlineAnnotationId(rects, midlineY, commentByAnnotationId, {
        maxFallbackDistance,
      });
      if (!nextId) {
        if (expandedCardId != null && scrollDrivenIdRef.current === expandedCardId) {
          scrollDrivenIdRef.current = null;
          setExpandedCardId(null);
        }
        return;
      }
      if (nextId === expandedCardId) return;

      scrollDrivenIdRef.current = nextId;
      setExpandedCardId(nextId);
    };

    const schedule = () => {
      captureBaselineAfterSettle();
      if (rafHandle != null) return;
      rafHandle = window.requestAnimationFrame(tick);
    };

    // Primary scroll source is the editor-stack (centerColumnRef); also listen
    // on window scroll + resize to be safe.
    const centerColumn = centerColumnRef?.current;
    centerColumn?.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);

    // Initial sync when comments first appear or sidebar opens. Suppress this after
    // feedback generation so comment refetches don't yank the sidebar while the user
    // is browsing generated comments; real editor scroll still schedules sync.
    if (!suppressMidlineInitialSyncRef.current) {
      schedule();
    }

    return () => {
      centerColumn?.removeEventListener("scroll", schedule);
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
      if (rafHandle != null) window.cancelAnimationFrame(rafHandle);
      if (baselineSettleTimerRef.current != null) {
        window.clearTimeout(baselineSettleTimerRef.current);
        baselineSettleTimerRef.current = null;
      }
    };
  }, [DOMDocument, showComments, waitingForFeedbackComments, visibleComments, expandedCardId, centerColumnRef, clearBaselineTimers]);

  useEffect(() => {
    if (!subscription.hasAccessToCleanCopy) return;
    if (!displayName) {
      DisplayName.get().then((name) => {
        setDisplayName(name);
      });
    }
    setSetToast(setToast);
  }, []);

  useEffect(() => {
    if (!subscription.hasAccessToCleanCopy) return;
    if (!commenter) {
      setCommenter(user);
    }
    // EditorContainer is only rendered for the document owner
    setIsDocumentOwner(true);
    setIsSharedDocument(false);
  }, [user]);

  useEffect(() => {
    if (!subscription.hasAccessToCleanCopy) return;
    if (editorContainerRef.current && editorRef.current) {
      window.editorRef = editorRef.current;
      // The editor lives in the main document now (same as ShareLinkPage).
      setDOMDocument(window.document);
    }
  }, [editorContainerRef.current, editorRef.current]);

  useEffect(() => {
    if (!subscription.hasAccessToCleanCopy) return;
    if (!documentId) return;
    if (!commentsLoaded) return;
    // Opening the sidebar for a chat deep-link is handled by the pending-navigation effect above.
    if (useProjectStore.getState().pendingChatCommentNavigation) return;
    // SW-8693 — Do not auto-close the sidebar while the user is creating a new comment.
    // The new-comment flow (HighlightMenu) sets showNewCommentBox=true and toggleComments(true)
    // before commentsLoaded resolves; closing here would destroy the in-progress comment box.
    if (useCommentStore.getState().showNewCommentBox) return;
    // SW-8335 — Dean's vote: "simply remain closed if you have closed that
    // comments section". Defer the open/close decision to a pure helper that
    // honors the persisted per-document close flag in projectStore.
    const userClosedSidebarByDocId = useProjectStore.getState().userClosedCommentSidebarByDocId;
    const shouldOpen = shouldAutoOpenCommentSidebar({
      documentId,
      commentCount: comments?.length ?? 0,
      userClosedSidebarByDocId,
      hasPendingChatCommentNavigation: false, // we already short-circuited above
    });
    // The `userInitiated: false` flag prevents this automatic toggle from
    // writing to the persisted per-document close flag — only explicit user
    // actions (badge click, highlight menu, etc.) should mark a document as
    // "user-closed".
    toggleComments(shouldOpen, { userInitiated: false });
  }, [documentId, commentsLoaded]);

  useEffect(() => {
    if (typeof onEditorLayoutChange !== "function") return;

    // Wait two frames so the comment sidebar mount/unmount and width reflow settle
    // before measuring editor layout.
    let frameOne = null;
    let frameTwo = null;
    frameOne = window.requestAnimationFrame(() => {
      frameTwo = window.requestAnimationFrame(() => {
        onEditorLayoutChange();
      });
    });

    return () => {
      if (frameOne != null) {
        window.cancelAnimationFrame(frameOne);
      }
      if (frameTwo != null) {
        window.cancelAnimationFrame(frameTwo);
      }
    };
  }, [showComments, onEditorLayoutChange]);

  const ProseGeneratorComponent = project.useScenes ? Draft : ProseGenerator;

  // Track the Draft panel's rendered height so the comment sidebar can offset below it
  useEffect(() => {
    const el = draftPanelRef.current;
    if (!el) {
      setDraftPanelHeight(0);
      return;
    }
    const observer = new ResizeObserver(([entry]) => {
      setDraftPanelHeight(entry.contentRect.height);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [documentId]);

  useEffect(() => {
    document.onkeydown = function (evt) {
      evt = evt || window.event;
      if (evt.key === "Escape") {
        setFocus(false);
      }
    };
  }, []);

  useEffect(() => {
    const ownerDocument = editorContainerRef.current?.ownerDocument || window.document;
    const ownerWindow = ownerDocument.defaultView || window;
    const prepareForPrint = () => createProjectPrintDocument(editorContainerRef.current);
    const cleanUpAfterPrint = () => removeProjectPrintDocument(ownerDocument);

    ownerWindow.addEventListener("beforeprint", prepareForPrint);
    ownerWindow.addEventListener("afterprint", cleanUpAfterPrint);

    return () => {
      ownerWindow.removeEventListener("beforeprint", prepareForPrint);
      ownerWindow.removeEventListener("afterprint", cleanUpAfterPrint);
      cleanUpAfterPrint();
    };
  }, []);

  useEffect(() => {
    setLastInvocation(recentInvocations.lastInvocationId);
  }, [recentInvocations]);

  useEffect(() => {
    setShowThumbs(!isStreaming && !requesting && !comparisonMode && lastInvocation && recentInvocations.lastInvocationType === "sceneProse");
  }, [isStreaming, lastInvocation, recentInvocations.lastInvocationType, comparisonMode]);

  return (
    <>
      <EditorContainerContainer
        className={classnames("editor-container-container", { focus: focus })}
        showThumbs={showThumbs}
        role="main"
        aria-label={editorLabel}
      >
        <h2 className="sr-only" id="heading-editor">{editorLabel}</h2>
        <StickyNoteAnchor anchorId={AnchorIds.EDITOR} />
        {!focus && (
          <a
            className="open-focus"
            onClick={(e) => setFocus(true)}
            src="/assets/noun-expand-4983045.svg"
          />
        )}
        {focus && (
          <a
            className="close-focus"
            onClick={(e) => setFocus(false)}
          >
            <SVG src="/assets/noun-minimize-1364472.svg" />
          </a>
        )}

        {focus && intro && (
          <div
            className={classnames("overlay-wrapper", { dismissed: dismissed })}
            onClick={(e) => {
              setDismissed(true);
              setTimeout(() => {
                setIntro(false);
              }, 250);
            }}
          >
            <div className="overlay"></div>
            <div className="relax">
              Relax. You're in <strong>focus mode</strong>.
            </div>
            <div className="esc">
              Press <strong>ESC</strong> to go back. Click anywhere to begin.
            </div>
          </div>
        )}
        {!focus && (
          <EditorToolbar
            editor={editor}
            editorReady={editorReady}
            onUndo={onUndo}
            onRedo={onRedo}
            range={range}
            leftBarCollapsed={leftBarCollapsed}
            documentId={documentId}
            editorComputedStyles={editorComputedStyles}
            editorToolbarRef={editorToolbarRef}
            toggleFindAndReplaceOpen={toggleFindAndReplaceOpen}
            setRightBarCollapsed={setRightBarCollapsed}
            hideLinkingMenu={hideLinkingMenu}
            ydoc={ydoc}
            toggleSpellCheck={toggleSpellCheck}
            onEditorLayoutChange={onEditorLayoutChange}
          />
        )}
        <LinkBubble editor={editor} />
        <div ref={titleWrapperRef}>
          {showEditableTitle &&
            (subscription?.hasAccessToChapterGlueBeta ? (
              <EditorHeaderContainer>
                <EditorContainerProvider titleEditorRef={titleEditorRef} titleMaxWidthOffset={commentTitleMaxWidthOffset}>
                  <EditableDocumentTitle
                    ref={editableDocumentTitleRef}
                    project={project}
                    documentId={documentId}
                    contents={contents}
                    editorComputedStyles={editorComputedStyles}
                    editor={editor}
                    insertEditor={insertEditor}
                    hasComments={subscription.hasAccessToCleanCopy && showComments}
                    commentSidebarWidth={commentSidebarWidth}
                  />
                  <DocumentLinkingMenu
                    project={project}
                    documentId={documentId}
                    hideLinkingMenu={hideLinkingMenu}
                    inTrashView={inTrashView}
                    editableDocumentTitleRef={editableDocumentTitleRef}
                  />
                </EditorContainerProvider>
              </EditorHeaderContainer>
            ) : (
              <EditorContainerProvider titleEditorRef={titleEditorRef} titleMaxWidthOffset={commentTitleMaxWidthOffset}>
                <EditableDocumentTitle
                  ref={editableDocumentTitleRef}
                  project={project}
                  documentId={documentId}
                  contents={contents}
                  editorComputedStyles={editorComputedStyles}
                  editor={editor}
                  insertEditor={insertEditor}
                  hasComments={subscription.hasAccessToCleanCopy && showComments}
                  commentSidebarWidth={commentSidebarWidth}
                />
              </EditorContainerProvider>
            ))}
        </div>
        {showThumbs && (
          <Thumbs
            invocationId={lastInvocation}
            key={lastInvocation}
            setFeedbackOpen={setFeedbackOpen}
            setFeedbackInvocation={setFeedbackInvocation}
          />
        )}
        {documentId && (
          <>
            <div ref={draftPanelRef}>
              <ProseGeneratorComponent
                key={`prose-generator-${documentId}`}
                editor={editor}
                centerColumnRef={centerColumnRef}
                editorToolbarRef={editorToolbarRef}
                editorRef={editorRef}
                editorEndRef={editorEndRef}
                editableDocumentTitleRef={editableDocumentTitleRef}
                projectId={project.id}
                documentId={documentId}
                comparisonEditorsRef={comparisonEditorsRef}
                comparisonInvocationIdRef={comparisonInvocationIdRef}
                streamBSceneIndexRef={streamBSceneIndexRef}
                comparisonMode={comparisonMode}
                setComparisonMode={setComparisonMode}
                setComparisonVisible={setComparisonVisible}
                addDraftPlaceholderCard={addDraftPlaceholderCard}
                appendDraftPlaceholderContent={appendDraftPlaceholderContent}
                linkDraftPlaceholderToCardId={linkDraftPlaceholderToCardId}
                finalizeDraftPlaceholder={finalizeDraftPlaceholder}
                removeDraftPlaceholderCard={removeDraftPlaceholderCard}
                onEditorLayoutChange={onEditorLayoutChange}
              />
            </div>
            <ChapterEditor
              key={`chapter-editor-${documentId}`}
              editor={editor}
              centerColumnRef={centerColumnRef}
              editorToolbarRef={editorToolbarRef}
              editorRef={editorRef}
              editorEndRef={editorEndRef}
              editableDocumentTitleRef={editableDocumentTitleRef}
              projectId={project.id}
              documentId={documentId}
            />
          </>
        )}

        {comparisonMode && (
          <DraftComparison
            ref={comparisonEditorsRef}
            onPick={handlePick}
            visible={comparisonVisible}
            isStreaming={isStreaming || requesting}
            editorComputedStyles={editorComputedStyles}
          />
        )}

        <div className={classnames("editor-container-wrapper", { hidden: comparisonVisible, "has-comments": subscription.hasAccessToCleanCopy && showComments })}>

          <div
            translate="no"
            ref={editorContainerRef}
            id="editorContainer"
            className={classnames("notranslate", { disabled })}
          >
            <div id="editor" />
          </div>
          {subscription.hasAccessToCleanCopy && showComments && (
            <CommentSidebar
              comments={visibleComments}
              onNavigateUp={handleNavigateUp}
              onNavigateDown={handleNavigateDown}
              onDeleteAll={handleDeleteAllComments}
              expandedCardId={expandedCardId}
              wiggleTargetCommentId={sidebarWiggleCommentId}
              onExpandCard={handleExpandCard}
              onScrollToAnnotation={handleScrollToAnnotation}
              onApply={handleApplyFeedback}
              onDismiss={handleDismissFeedback}
              onDeleteComment={handleDeleteUserComment}
              onUndoComment={handleUndoComment}
              canUndoComment={canUndoComment}
              canNavigateUp={visibleComments.length > 0 && (expandedCardId == null || visibleComments.findIndex(c => c.id === expandedCardId) > 0)}
              canNavigateDown={
                (visibleComments.length > 0 &&
                  (expandedCardId == null ||
                    visibleComments.findIndex(c => c.id === expandedCardId) < visibleComments.length - 1 ||
                    hasMoreComments)) ||
                hasMoreComments
              }
              draftPanelHeight={proseGeneratorActive ? draftPanelHeight : 0}
              newCommentInsertIndex={newCommentInsertIndex}
              documentId={documentId}
              commentLocation="Editor"
              selectedFilter={selectedCommentFilter}
              onFilterChange={setSelectedCommentFilter}
              commentCounts={commentCounts}
              commentsLoaded={commentsLoaded}
              isLoadingComments={commentsLoading}
              hasMoreComments={hasMoreComments}
              isLoadingMoreComments={isLoadingMoreComments}
              onLoadMoreComments={loadMoreComments}
            >
              {showNewCommentBox && (
                <NewComment
                  newComment={newComment}
                  setNewComment={setNewComment}
                  documentId={documentId}
                  commentLocation="Editor"
                  onCommentCreated={(commentId) => {
                    setExpandedCardId(commentId);
                  }}
                />
              )}
            </CommentSidebar>
          )}
        </div>
        <GrammarPopover editor={editor} />
        <StyleSettingsDialog />
        <StickyNoteAnchor anchorId={AnchorIds.EDITOR_END} />
      </EditorContainerContainer>
      {focus && <BackgroundContainer></BackgroundContainer>}
      <FeedbackApplyConfirmationDialog
        details={feedbackApplyConfirmation}
        onCancel={() => settleFeedbackApplyConfirmation(false)}
        onConfirm={() => settleFeedbackApplyConfirmation(true)}
      />
    </>
  );
}
